import { Expose, Transform, Type } from 'class-transformer';
import { UserEntity } from '@core/entities/user.entity';
import { BaseEntity, PaginatedEntity } from '@core/entities/_extra';
import { SurveyNotificationOption, SurveyOptions, SurveyStatus, SurveyType, SurveyViewOption } from '@core/enums';
import { BeautifyArrayOfNumbers } from '@core/utils';
import { SafeUrl } from '@angular/platform-browser';

export class SurveysList extends PaginatedEntity {
  @Type(() => SurveyEntity)
  survey: SurveyEntity[];
}

export class SurveyEntity extends BaseEntity {
  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  title = '';

  @Expose()
  @Transform((item) => encodeURIComponent(item.value), { toPlainOnly: true })
  @Transform((item) => decodeURIComponent(item.value), { toClassOnly: true })
  description = '';

  @Expose({ name: 'survey_status' })
  status: SurveyStatus = SurveyStatus.Active;

  @Expose({ name: 'surveyStartDate' })
  startDate = new Date().toDateString();

  @Expose({ name: 'surveyEndDate' })
  endDate = new Date().toDateString();

  @Expose({ name: 'user_name' })
  @Type(() => UserEntity)
  owner: UserEntity;

  // more properties required for POST and PUT requests

  @Expose({ name: 'surveyOption' })
  option: SurveyOptions = SurveyOptions.SingleChoice;

  /**
   * Survey Type: For POST and PUT requests
   */
  @Expose({ name: 'surveyType' })
  type: SurveyType = SurveyType.Club;

  /**
   * Survey Type: For GET details request
   */
  @Expose({ name: 'survey_type' })
  @Type(() => SurveyTypeEntity)
  surveyType: SurveyTypeEntity[] = [];

  groupType: number[] = [];

  @Expose({ name: 'surveyViewOption' })
  viewOption: SurveyViewOption = SurveyViewOption.Always;

  @Expose({ name: 'additional_anonymous_voting' })
  additionalAnonymousVoting = false;

  @Expose({ name: 'additional_cast_vote' })
  additionalCastVote = false;

  @Expose({ name: 'surveyNotificationOption' })
  @Transform((item) => BeautifyArrayOfNumbers(item.value))
  notificationOption: SurveyNotificationOption[] = [];

  /**
   * Survey Answers: For POST and PUT requests
   */
  @Expose({ name: 'survey_Answers' })
  answers: string[] = [];

  /**
   * Survey Answers: For GET details request
   */
  @Type(() => SurveyAnswerEntity)
  surveyAnswer: SurveyAnswerEntity[] = [];

  @Expose({ name: 'surveyResult' })
  @Type(() => SurveyResultsEntity)
  surveyResults: SurveyResultsEntity[];

  /**
   * Survey Tags: For POST and PUT requests
   */
  tags: string[] = [];

  /**
   * Survey Tags: For GET details request
   */
  @Type(() => SurveyTagEntity)
  surveyTags: SurveyTagEntity[] = [];

  image: string;
  imageUrl: SafeUrl;

  constructor(initialData?: Partial<SurveyEntity>) {
    super();
    this.initEntity(initialData);
  }

  get percentage(): number {
    const startDate = new Date(this.startDate);
    const endDate = new Date(this.endDate);

    const totalDifference = endDate.getTime() - startDate.getTime();

    const remainingDays = endDate.getTime() - new Date().getTime();

    if (remainingDays <= 0) {
      return 100;
    }

    return ((totalDifference - remainingDays) / totalDifference) * 100;
  }

  get color(): string {
    const percentage = this.percentage;

    if (percentage < 40) {
      return 'var(--light-teal)';
    } else if (percentage >= 40 && percentage < 80) {
      return 'var(--dark-yellow)';
    } else {
      return 'var(--light-orange)';
    }
  }
}

// Used when fetching Survey Details
export class SurveyTypeEntity {
  id: number;
  surveyId: number;
  surveyTypeId: number;
  surveyType: SurveyType;

  @Expose({ name: 'survey_group' })
  surveyGroup: {
    id: number;
    name: string;
  };
}

// Used when fetching Survey Details
export class SurveyTagEntity {
  id: number;

  @Expose({ name: 'survey_id' })
  surveyId: number;

  @Expose({ name: 'tag_id' })
  tagId: number;

  @Expose({ name: 'tag_name' })
  tagName: string;
}

export class SurveyAnswerEntity {
  id: number;
  surveyId: number;
  surveyAnswer: string;
}

export class SurveyResultsEntity {
  id?: number;
  surveyId: number;
  surveyAnswerId: string;
  surveyType: string | SurveyType;
  SurveyTypeId: string;
  @Expose({ name: 'team_id' })
  teamId: number;
  @Expose({ name: 'user_id' })
  userId: number;

  @Expose({ name: 'survey_answer' })
  @Type(() => SurveyAnswerEntity)
  surveyAnswer?: SurveyAnswerEntity;

  @Expose({ name: 'user_details' })
  @Type(() => UserEntity)
  userDetails?: Partial<UserEntity>;
}

/**
 * GET Survey Results Based Entities
 */

export class SurveyResultDetailsEntity {
  @Expose({ name: 'result' })
  @Type(() => SurveyEntity)
  surveyDetails: SurveyEntity[];

  @Type(() => SurveyResultAnswerCountEntity)
  answerCount: SurveyResultAnswerCountEntity[];

  @Expose({ name: 'TotalCount' })
  totalCount: number;

  userCount: number;
}

export class SurveyResultAnswerCountEntity {
  @Expose({ name: 'AnswerId' })
  answerId: number;
  count: number;

  // for Ui:
  percentageValue = 0;
}

export class SurveyAnswerDetailsEntity {
  @Expose({ name: 'result' })
  @Type(() => SurveyResultsEntity)
  votingResult: SurveyResultsEntity[];

  @Expose({ name: 'SurveyAnswer' })
  @Type(() => SurveyAnswerEntity)
  surveyAnswer: SurveyAnswerEntity[];

  @Expose({ name: 'Count' })
  count: number;

  @Expose({ name: 'TotalCount' })
  totalCount: number;
}
